import { Inject } from '@odinjs/odin';
import { ActionAPI, ContextualBarAPI, FetchAPI, RepeaterAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnClick, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HistoryParameters } from '@philips-internal/oswald-ui-core/lib/services/history-service';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { ApprovalItemDetailsAPI } from '@philips-internal/pending-approval-components';

import { ApplicationRoute, ModulesRoute } from '../../constants/routes';
import ApprovalContext from '../../services/ApprovalService';
import ReportService from '../../services/ReportService';
import BaseController from '../BaseController';

import ApprovalProcessModal from './approval-process/approval-process-modal';
import { ApprovalItem } from './approval-types';
import { getOriginByType, Situation, translateReason, translateSituation } from './approvals-business';

import './approvals-item.css';

export const approvalForwardModelID = 'approval-forward-model';
export const approvalDisapproveModalID = 'approval-disapprove-modal';
interface ApprovalRouteParams extends HistoryParameters {
  id: string
  processId: string
}

@Controller({ fragment: 'approval-item', component: 'layout-approval-item' })
export default class ApprovalsItemController extends BaseController {

  @Inject('approval-item-details')
    approvalItemDetails: ApprovalItemDetailsAPI;

  @Inject('fetch-items')
    fetchItems: FetchAPI;

  @Inject('repeater-items')
    repeaterData: RepeaterAPI;

  @Inject('action-approve')
    approveAction: ActionAPI;

  @Inject('action-disapprove')
    disapproveAction: ActionAPI;

  @Inject('action-forward')
    forwardAction: ActionAPI;

  @Inject('action-request-opinion')
    requestOpinionAction: ActionAPI;

  @Inject('action-inform-opinion')
    informOpinionAction: ActionAPI;

  @Inject('contextual-bar')
    contextualBar: ContextualBarAPI;

  @OnMount('layout-approval-item')
  onFragmentMount(): void {
    const { id, processId } = this.historyService.getCurrentRouteParams() as ApprovalRouteParams;
    this.context.set({ id, processId });
  }

  @OnMount('fetch-items')
  onBeforeFetchApprovalItem(): void {
    const { processId } = this.historyService.getCurrentRouteParams() as ApprovalRouteParams;
    const { releaseDate, situation, reason, totalApprovalValue, originStock, targetStock, documentNumber, observation } = ApprovalContext.get();
    this.prepareActions();
    this.approvalItemDetails.setData({
      proccessNumber: processId,
      deliveryDate: releaseDate,
      situation: translateSituation(situation as Situation),
      dsSituation: situation as Situation,
      reason: translateReason(reason),
      totalApprovalValue: totalApprovalValue,
      observation: observation,
      originStock: originStock,
      documentNumber: documentNumber,
      targetStock: targetStock,
    });

  }

  prepareActions(): void {
    const { situation, usernameOpinion } = ApprovalContext.get();
    const allowedActions = ['PENDING', 'FORWARD'];
    if (!allowedActions.includes(situation)) {
      this.disapproveAction.setEnabled(false);
      this.approveAction.setEnabled(false);
      this.forwardAction.setEnabled(false);
      this.requestOpinionAction.setEnabled(false);
      this.informOpinionAction.setEnabled(false);
    }
    if (usernameOpinion != foundationContext.getUser()?.username) {
      this.informOpinionAction.setEnabled(false);
    } else {
      this.disapproveAction.setEnabled(false);
      this.approveAction.setEnabled(false);
      this.forwardAction.setEnabled(false);
      this.requestOpinionAction.setEnabled(false);
    }
  }

  @OnAfterFetch('fetch-items')
  onAfterFechApprovalItem(event: FetchEvent): void {
    const approvalItems = event.data as ApprovalItem[];
    if (approvalItems.length === 0) {
      return;
    }
    const item = approvalItems[0];
    this.approvalItemDetails.setData({
      approvationNumber: item.approvalId,
      requester: item.requestName,
      dsOrigin: getOriginByType(item.type),
      origin: item.type,
    });
    ApprovalContext.set({ ...ApprovalContext.get(), orderType: item.type, purchaseRequestNumber: item.purchaseRequestNumber });
  }

  @OnClick('link-stages')
  onClickSteps(): void {
    const { id, processId } = this.historyService.getCurrentRouteParams();
    const approvalList = `${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}`;

    applicationContext.redirect(`${approvalList}${ApplicationRoute.APPROVAL_ITEM}${ApplicationRoute.PURCHASE_APPROVAL}`,
      { id, processId }, { scrollToTop: true });
  }

  @OnClick('action-approve')
  onActionApproveClick(event: ClickEvent): void {
    const actionApprove = event.target as ActionAPI;
    console.info(actionApprove.getAlias());
    const { id, processId } = this.historyService.getCurrentRouteParams() as ApprovalRouteParams;
    if (id && processId) {
      ApprovalProcessModal.openDialog(id, processId);
    }
  }

  @OnClick('action-disapprove')
  onActionDesapproveClick(event: ClickEvent): void {
    if (event) {
      const { id, processId } = this.historyService.getCurrentRouteParams();
      applicationContext.redirect(`${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}${ApplicationRoute.APPROVAL_ITEM}${ApplicationRoute.DISAPPROVE}`,
        { id, processId }, { scrollToTop: true });
    }
  }

  @OnClick('action-forward')
  onActionFowardClick(event: ClickEvent): void {
    if (event) {
      const { id, processId } = this.historyService.getCurrentRouteParams();
      applicationContext.redirect(`${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}${ApplicationRoute.APPROVAL_ITEM}${ApplicationRoute.FORWARD}`,
        { id, processId }, { scrollToTop: true });
    }
  }

  @OnClick('action-inform-opinion')
  onActionInformOpinionClick(event: ClickEvent): void {
    if (event) {
      const { id, processId } = this.historyService.getCurrentRouteParams();
      const itemLocation = `${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}${ApplicationRoute.APPROVAL_ITEM}`;
      applicationContext.redirect(`${itemLocation}${ApplicationRoute.INFORM_OPINION}`, { id, processId }, { scrollToTop: true });
    }
  }

  @OnClick('action-request-opinion')
  onActionRequestOpinionClick(event: ClickEvent): void {
    const actionRequestOpinion = event.target as ActionAPI;
    if (actionRequestOpinion) {
      const { id, processId } = this.historyService.getCurrentRouteParams();
      applicationContext.redirect(`${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}${ApplicationRoute.APPROVAL_ITEM}${ApplicationRoute.OPINION_REQUEST}`,
        { id, processId }, { scrollToTop: true });
    }
  }

  @OnClick('action-report')
  async onActionReportClick(): Promise<void> {
    const { id } = this.historyService.getCurrentRouteParams();
    await ReportService.generateReport(String(id));
  }

  @OnClick('action-attachment')
  onActionAttachmentClick(): void {
    const { id, processId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(`${ModulesRoute.APPROVALS}${ApplicationRoute.APPROVAL_LIST}${ApplicationRoute.APPROVAL_ITEM}${ApplicationRoute.APPROVALS_ATTACHMENTS}`,
      { id, processId }, { scrollToTop: true });
  }
}
